import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { menuActivityQueryKey } from '@/lib/menu/useMenuActivity';
import { RELEASE_NOTE_ROWS } from '@/lib/releaseNotes';
import { FRONT_BUILD, releaseNoteLinksQueryKey } from '@/lib/version';
import { SCREENS } from '@/screens';

/**
 * 화면 진입(VIEW)을 서버에 알리는 비콘.
 *
 * SPA 는 라우트 전환이 특정 API 를 치지 않아 "이 화면을 봤다" 를 서버가 알 방법이 없다. 그래서
 * 경로가 바뀔 때마다 `POST /v1/activity` 로 `menuKey`(화면 식별자)를 보낸다. 메뉴 골격은 프론트가
 * 쥐고 있으므로(screens.tsx) 화면 식별은 여기서 가장 정확하다.
 *
 * 부수 기록이므로 실패해도 삼킨다 — 사용자의 화면 이용을 막을 이유가 없다. 인가와도 무관하다:
 * `menuKey` 는 통계용이고 권한은 백엔드가 매 요청마다 판정한다.
 *
 * ## 사이드바 New 배지도 이 비콘으로 접힌다 (V220)
 *
 * 서버가 같은 요청으로 「이 사람이 이 메뉴를 봤다」 를 남기고(`sys_menu_visits`), 그 뒤로 바뀐 것이
 * 없는 메뉴는 `/v1/menus/activity` 응답에서 빠진다. 배지를 위해 요청을 따로 보내지 않는 이유가
 * 이것이다 — 화면에 들어갔다는 사실은 <b>이미 여기로 온다.</b>
 *
 * 보낸 뒤 배지 질의를 무르는(invalidate) 것은 **응답을 기다린 다음**이다. 먼저 물으면 서버가 아직
 * 기록하기 전의 답을 받아 캐시에 넣고, 배지는 다음 5분 갱신까지 그대로 남는다.
 */

/** 경로 → 화면 키. screens.tsx 를 한 번만 훑어 만든다(여러 경로가 같은 menuKey 를 가리켜도 무방). */
const MENU_KEY_BY_PATH = new Map(SCREENS.map((screen) => [screen.path, screen.menuKey]));

/**
 * 이 번들의 노트 키를 탭마다 한 번 보낸다 (V448). 서버는 아직 판이 없는 키를 지금 판에 저장하고,
 * 「Job 버전별」 은 그 저장을 읽는다 — 같은 번들은 같은 키를 보내므로 서버도 번호당 한 번만 본다.
 */
let notesReported = false;

function reportReleaseNotes(onSaved: () => void) {
  if (notesReported) return;
  notesReported = true;
  void apiFetch<void>('/v1/releases/notes/report', {
    method: 'POST',
    body: { frontBuild: FRONT_BUILD, notes: RELEASE_NOTE_ROWS.map((row) => ({ key: row.key, date: row.date })) },
  })
    // 노트 창이 보고 전에 읽어 둔 목록을 버린다 — 방금 저장된 줄이 「판 미지정」 에 남아 있지 않게.
    .then(onSaved)
    .catch(() => {
      // 탭당 한 번만 묻는다 — 옛 서버(404)에 화면 이동마다 다시 보내지 않는다. 다른 탭·사람이 또 보낸다.
    });
}

export function useActivityBeacon() {
  const { pathname } = useLocation();
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const authenticated = session?.authenticated ?? false;
  // 개발용 목 세션(백엔드 없음)에서는 보낼 곳이 없다.
  const isMock = session?.user?.id === 'dev-mock';

  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    if (!authenticated || isMock) return;
    // 같은 경로를 두 번 보내지 않는다(StrictMode 이중 실행·리렌더 방지).
    if (lastSent.current === pathname) return;
    lastSent.current = pathname;

    const menuKey = MENU_KEY_BY_PATH.get(pathname);

    void apiFetch<void>('/v1/activity', {
      method: 'POST',
      /*
        빌드 번호를 함께 보낸다 (V361). 서버가 이것과 자기 번호를 짝지어 「관측된 판」 한 줄을
        남기고, 릴리즈 노트의 「Job 버전별」 보기가 그 목록을 경계로 쓴다. 번호는 젠킨스가
        빌드할 때 정해지므로 사람이 노트에 적을 수 없다 — 떠 있는 판이 스스로 알리는 편이 맞다.
      */
      body: { menuKey, path: pathname, frontBuild: FRONT_BUILD },
    })
      .then(() => {
        reportReleaseNotes(() => void queryClient.invalidateQueries({ queryKey: releaseNoteLinksQueryKey }));
        // 메뉴에 매이지 않은 화면(홈)은 접을 배지가 없다.
        if (menuKey) void queryClient.invalidateQueries({ queryKey: menuActivityQueryKey });
      })
      .catch(() => {
        // 비콘 실패는 조용히 넘긴다. 다음 화면 이동에서 다시 시도된다.
      });
  }, [authenticated, isMock, pathname, queryClient]);
}
